import React from "react";

/**
 * Analytics presentation primitives.
 *
 * Charts are hand-built from CSS bars and SVG paths rather than a chart
 * library, because the shapes needed here are simple and the design depends on
 * exact control — notably annotating the loss *between* funnel steps, which no
 * off-the-shelf bar chart does.
 *
 * The design rules live in these components so a new tile cannot break them:
 * funnel steps are ordered bars (never a pie — you cannot read drop-off from
 * wedges), no chart carries a second Y axis, and an empty result always
 * explains itself rather than rendering a blank axis.
 */

/* ---------------------------------------------------------------- format */

export const formatNumber = (value) =>
  value === null || value === undefined ? "—" : Number(value).toLocaleString();

export const formatPercent = (value, digits = 1) =>
  value === null || value === undefined ? "—" : `${(value * 100).toFixed(digits)}%`;

/**
 * Below this many observations a percentage is noise dressed as precision.
 * "50.0%" from two people is technically true and practically meaningless, and
 * a dashboard that prints it will get acted on.
 */
export const MIN_SAMPLE = 30;

/**
 * A rate, shown only when enough people sit behind it.
 *
 * Under the threshold it falls back to the raw counts — "3 of 8" is honest
 * where "37.5%" is not, because the reader can see for themselves how little
 * evidence there is.
 */
export const formatRate = (numerator, denominator, { digits = 1 } = {}) => {
  const n = Number(numerator ?? 0);
  const d = Number(denominator ?? 0);
  if (d === 0) return "—";
  if (d < MIN_SAMPLE) return `${formatNumber(n)} of ${formatNumber(d)}`;
  return `${((n / d) * 100).toFixed(digits)}%`;
};

/** True when a figure rests on too few people to be read as a rate. */
export const isSmallSample = (denominator) =>
  Number(denominator ?? 0) > 0 && Number(denominator) < MIN_SAMPLE;

/**
 * Period-over-period change, for the KPI strip.
 *
 * Returns null when there is nothing meaningful to say — no comparison window,
 * or a previous value of zero, where "up ∞%" would be theatre rather than
 * information.
 */
export const deriveTrend = (current, previous, { higherIsBetter = true } = {}) => {
  if (current === null || current === undefined) return null;
  if (previous === null || previous === undefined) return null;
  if (previous === 0) return null;

  const change = (current - previous) / Math.abs(previous);
  if (Math.abs(change) < 0.005) return { tone: "flat", text: "no change" };

  const up = change > 0;
  const good = up === higherIsBetter;
  return {
    tone: good ? "up" : "down",
    text: `${up ? "▲" : "▼"} ${Math.abs(change * 100).toFixed(0)}%`,
  };
};

/** Date columns arrive from the API as { value: 'YYYY-MM-DD' }. */
export const readDate = (value) =>
  value && typeof value === "object" && "value" in value ? value.value : value;

/**
 * Turns raw values from the event data into words a reader recognises.
 *
 * The app records terse machine values ('transcode', 'D7', 'IOS'). Those are
 * right for storage and wrong for a screen someone is meant to act on — this
 * dashboard is read by people who did not write the instrumentation, and
 * "transcode" tells them nothing about what broke.
 *
 * Anything unmapped falls through with its first letter capitalised, so a new
 * value added in the app degrades to something readable rather than vanishing.
 */
const LABELS = {
  // Where an upload failed
  transcode: "Video processing",
  network: "Connection",
  record: "Recording",
  premium: "Subscription needed",
  unknown: "Not recorded",

  // How someone signed in
  email: "Email",
  google: "Google",
  apple: "Apple",
  facebook: "Facebook",
  phone: "Phone",

  // Retention windows
  D1: "After 1 day",
  D7: "After 1 week",
  D30: "After 1 month",

  // Why a recording ended
  user: "Backed out",
  error: "Recorder failed",

  // Onboarding entry point
  gate: "After signing in",
  preview: "From the sign-in screen",
};

export const humanLabel = (value) => {
  if (value === null || value === undefined || value === "") return "Not recorded";
  return LABELS[value] ?? String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

/** 2026-07-18 → 18 Jul. Axis labels need to be short, not precise. */
export const shortDate = (iso) => {
  if (!iso) return "";
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? String(iso)
    : date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

/* ------------------------------------------------------------ containers */

export const Pill = ({ tone = "good", children }) => (
  <span className={`pill pill--${tone}`}>{children}</span>
);

export const SectionHead = ({ question, title, description, id, pill }) => (
  <div className="section__head">
    <div className="section__title">
      <p className="section__q">{question}</p>
      <h2 id={id}>{title}</h2>
      {description && <p>{description}</p>}
    </div>
    {pill}
  </div>
);

/**
 * A chart container. `isEmpty` is required rather than inferred: several views
 * are legitimately empty until enough history accumulates, and a blank axis is
 * indistinguishable from a broken dashboard.
 */
export const Card = ({ title, description, pill, isEmpty, emptyMessage, children }) => (
  <div className="card">
    <div className="card__head">
      <div>
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
      {pill}
    </div>
    {isEmpty ? (
      <div className="card__empty">
        <p>{emptyMessage || "No data for this period yet."}</p>
      </div>
    ) : (
      children
    )}
  </div>
);

export const StatCard = ({ label, value, hint, trend }) => (
  <div className="kpi">
    <span className="kpi__label">{label}</span>
    <div className="kpi__row">
      <span className="kpi__value">{value}</span>
      {trend && <span className={`kpi__delta ${trend.tone}`}>{trend.text}</span>}
    </div>
    {hint && <span className="kpi__hint">{hint}</span>}
  </div>
);

/* ----------------------------------------------------------------- bars */

/**
 * Funnel steps with the loss annotated between them.
 *
 * The gap between two bars carries the number people actually act on — "84%
 * continue, 707 lost" — so it is stated rather than left to be inferred by
 * comparing bar lengths.
 *
 * `steps` must already be in funnel order; ordering is data, not presentation.
 */
export const FunnelSteps = ({ steps }) => {
  const top = Math.max(...steps.map((s) => s.users), 1);

  return (
    <div className="funnel">
      {steps.map((step, index) => {
        const next = steps[index + 1];
        // A step with no users cannot have a meaningful continuation rate;
        // showing "0% continue" from an empty step would invent a finding.
        const rate = next && step.users > 0 ? next.users / step.users : null;
        const lost = next && step.users > 0 ? step.users - next.users : 0;

        return (
          <React.Fragment key={step.step_name}>
            <div className="step">
              <span className="step__label">{step.step_name}</span>
              <div className="step__track">
                <div
                  className="step__bar"
                  style={{ width: `${(step.users / top) * 100}%` }}
                />
              </div>
              <span className="step__val">{formatNumber(step.users)}</span>
            </div>

            {next && (
              <div className="drop">
                <span />
                <span className="drop__note">
                  <span className="drop__rule" />
                  {rate === null ? (
                    "—"
                  ) : (
                    <>
                      {formatPercent(rate, 0)} continue
                      {lost > 0 && (
                        <>
                          {" · "}
                          <b>{formatNumber(lost)} left</b>
                        </>
                      )}
                    </>
                  )}
                </span>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Ranked horizontal bars. Opacity steps down the list so rank reads even
 * where bar lengths are close.
 */
export const BarList = ({ items, tone = "accent", isPercent }) => {
  const top = Math.max(...items.map((i) => i.value), isPercent ? 0.0001 : 1);

  return (
    <div className="bars">
      {items.map((item, index) => (
        <div className="bar-row" key={item.label}>
          <span className="bar-row__label" title={item.label}>
            {item.label}
          </span>
          <div className="bar-row__track">
            <div
              className="bar-row__fill"
              style={{
                width: `${Math.max((item.value / top) * 100, item.value > 0 ? 2 : 0)}%`,
                background: `var(--${tone})`,
                opacity: Math.max(1 - index * 0.16, 0.34),
              }}
            />
          </div>
          <span className="bar-row__val">
            {isPercent ? formatPercent(item.value) : formatNumber(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ---------------------------------------------------------------- charts */

const VIEW_W = 600;
const VIEW_H = 180;

/** Maps values to a path across a fixed viewBox, sharing one scale. */
const buildPath = (values, max, close) => {
  if (values.length === 0) return "";
  const stepX = values.length > 1 ? VIEW_W / (values.length - 1) : 0;
  const y = (v) => VIEW_H - 14 - ((v ?? 0) / max) * (VIEW_H - 28);
  const points = values.map((v, i) => `${i * stepX},${y(v)}`);
  const line = `M${points.join(" L")}`;
  return close ? `${line} L${VIEW_W},${VIEW_H} L0,${VIEW_H} Z` : line;
};

/**
 * A small area chart. Takes up to two series and deliberately offers no second
 * Y axis — two scales on one plot is the most misread chart form there is. Both
 * series share one scale so the gap between them is honest.
 */
export const AreaChart = ({ data, xKey, series, label, markers = [] }) => {
  const max = Math.max(
    ...series.flatMap((s) => data.map((row) => row[s.key] ?? 0)),
    1
  );

  const first = shortDate(data[0]?.[xKey]);
  const last = shortDate(data[data.length - 1]?.[xKey]);

  // Release markers, positioned by matching their date to a point on the axis.
  // Anything outside the plotted range is dropped rather than clamped to an
  // edge, where it would imply a release happened on a day it did not.
  const stepX = data.length > 1 ? VIEW_W / (data.length - 1) : 0;
  const placedMarkers = markers
    .map((marker) => {
      const index = data.findIndex((row) => row[xKey] === marker.date);
      return index === -1 ? null : { ...marker, x: index * stepX };
    })
    .filter(Boolean);

  return (
    <>
      <ul className="legend">
        {series.map((s) => (
          <li key={s.key}>
            <i style={{ background: `var(--${s.tone})` }} />
            {s.label}
          </li>
        ))}
      </ul>

      <svg
        className="chart"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={label}
      >
        {[45, 90, 135].map((y) => (
          <line key={y} x1="0" y1={y} x2={VIEW_W} y2={y} stroke="var(--line-2)" />
        ))}
        {placedMarkers.map((marker) => (
          <line
            key={marker.label}
            x1={marker.x}
            y1="0"
            x2={marker.x}
            y2={VIEW_H}
            stroke="var(--ink-3)"
            strokeWidth="1"
            strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {series.map((s) => {
          const values = data.map((row) => row[s.key]);
          return (
            <g key={s.key}>
              <path d={buildPath(values, max, true)} fill={`url(#grad-${s.tone})`} />
              <path
                d={buildPath(values, max, false)}
                fill="none"
                stroke={`var(--${s.tone})`}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
      </svg>

      <div className="chart__axis">
        <span>{first}</span>
        <span>{last}</span>
      </div>

      {/* Named below the plot rather than on it — labels inside a small chart
          collide with the lines they describe. */}
      {placedMarkers.length > 0 && (
        <p className="chart__markers">
          <span className="chart__marker-key" /> {placedMarkers.map((m) => m.label).join(" · ")}
        </p>
      )}
    </>
  );
};

/** Gradient defs, mounted once. Referencing defs inside a hidden or unmounted
 *  subtree is unreliable, so these live at the page root. */
export const ChartDefs = () => (
  <svg className="defs" aria-hidden="true" focusable="false">
    <defs>
      {["accent", "clay", "crit"].map((tone) => (
        <linearGradient key={tone} id={`grad-${tone}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={`var(--${tone})`} stopOpacity="0.26" />
          <stop offset="100%" stopColor={`var(--${tone})`} stopOpacity="0" />
        </linearGradient>
      ))}
    </defs>
  </svg>
);
